/// <amd-module name="VSSUI/Components/MouseWithin/MouseWithin" />
import * as React from "react";

import { isFunctionalChildren } from "VSSUI/Util";
import { IMouseWithin, IMouseWithinProps, IMouseWithinStatus } from "./MouseWithin.Props";

export class MouseWithin extends React.Component<IMouseWithinProps> implements IMouseWithin {
    public static defaultProps = {
        updateStateOnMouseChange: true
    };

    private enterTimeout = 0;
    private delayTimeout = 0;
    private mouse: boolean = false;

    public render(): JSX.Element {
        const newProps: Partial<IMouseWithinStatus> = {
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave
        };

        if (isFunctionalChildren<IMouseWithinStatus>(this.props.children)) {
            const child: (props: IMouseWithinStatus) => JSX.Element = this.props.children;

            // For functional components we pass the hasMouse attribute as well.
            newProps.hasMouse = this.mouse;

            return child(newProps as IMouseWithinStatus);
        } else {
            const child = React.Children.only(this.props.children) as React.DetailedReactHTMLElement<any, HTMLElement>;
            return React.cloneElement(child, { ...child.props, ...newProps }, child.props.children);
        }
    }

    public componentWillUnmount() {
        if (this.enterTimeout) {
            window.clearTimeout(this.enterTimeout);
        }

        if (this.delayTimeout) {
            window.clearTimeout(this.delayTimeout);
        }
    }

    /**
     * hasMouse returns true if the mouse is contained within the component
     * hierarchy. This includes portals, the element may or may not
     * be a direct descendant of the component in the DOM structure.
     */
    public hasMouse(): boolean {
        return this.mouse;
    }

    private mouseEntered(event: React.MouseEvent<HTMLElement>) {
        // If we are tracking the mouse state we will force a component update.
        if (this.props.updateStateOnMouseChange) {
            this.forceUpdate();
        }

        if (this.props.onMouseEnter) {
            this.props.onMouseEnter(event);
        }
    }

    private mouseLeft(event: React.MouseEvent<HTMLElement>) {
        // If we are tracking the mouse state we will force a component update.
        if (this.props.updateStateOnMouseChange) {
            this.forceUpdate();
        }

        if (this.props.onMouseLeave) {
            this.props.onMouseLeave(event);
        }
    }

    /**
     * onMouseEnter method that should be attached to the onMouseEnter handler of the
     * continer's root element.
     */
    private onMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        // If the mouse is just entering one of the child components and not just moving
        // from one child to another we will call the onMouseEnter delegate if supplied.
        if (!this.mouse) {
            this.mouse = true;

            // Clear any pending leave if we have left and re-entered the component during
            // the leaveTimeout.
            if (this.delayTimeout) {
                window.clearTimeout(this.delayTimeout);
                this.delayTimeout = 0;
            }

            if (this.props.enterDelay) {
                event.persist();
                // persist does not preserve the currentTarget so we do that manually
                const currentTarget = event.currentTarget;

                this.enterTimeout = window.setTimeout(() => {
                    this.enterTimeout = 0;
                    const newCurrentTarget = event.currentTarget;
                    event.currentTarget = currentTarget;
                    this.mouseEntered(event);
                    event.currentTarget = newCurrentTarget;
                }, this.props.enterDelay);
            } else {
                this.mouseEntered(event);
            }
        }
    };

    /**
     * onMouseLeave method that should be attached to the onMouseLeave handler of the
     * container's root element.
     */
    private onMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
        if (this.mouse) {
            this.mouse = false;

            // Clear any pending enterTimeout if we didnt stay over the element long enough.
            if (this.enterTimeout) {
                window.clearTimeout(this.enterTimeout);
                this.enterTimeout = 0;
            }

            if (this.props.leaveDelay) {
                event.persist();
                this.delayTimeout = window.setTimeout(() => {
                    this.delayTimeout = 0;
                    this.mouseLeft(event);
                }, this.props.leaveDelay);
            } else {
                this.mouseLeft(event);
            }
        }
    };
}
